import { getRamseyConnectBaseUrl } from '../../../shared/utils';
import { TaxAdviceOptions } from '../enums';

/**
 * Segment `question_source` for chats opened from the advice step. Ramsey
 * Connect uppercases the `source` param and swaps hyphens for underscores before
 * recording it, so this lands in analytics as `TAX_REFERRAL_ADVICE`.
 */
const RAMSEY_CONNECT_QUESTION_SOURCE = 'tax-referral-advice';

/**
 * Self-identifying UTMs stamped on the Ramsey Connect hand-off so Ramsey
 * Connect's analytics can attribute the session to this step, independent of whatever
 * campaign UTMs the fan originally arrived with on the referral form.
 */
const RAMSEY_CONNECT_UTM_SOURCE = 'tax_referral_form';
const RAMSEY_CONNECT_UTM_MEDIUM = 'internal_referral';
const RAMSEY_CONNECT_UTM_CAMPAIGN = 'ramsey_connect_advice_step';

interface AdviceTopicContent {
  heading: string;
  question: string;
  utmContent: string;
}

/**
 * Shown when the fan picked more than one advice topic: the heading drops to
 * the general one while the question still comes from the first topic they
 * picked, because the chat can only be seeded with a single opening question.
 */
const MULTIPLE_TOPICS_HEADING = 'Ask Ramsey AI your tax question.';
const MULTIPLE_TOPICS_UTM_CONTENT = 'multiple_topics';

/**
 * Every `TaxAdviceOptions` value except `other`, which has no question of its
 * own — the flow never routes here when it's the only selection.
 */
type MappedAdviceTopic = Exclude<
  (typeof TaxAdviceOptions)[keyof typeof TaxAdviceOptions]['value'],
  typeof TaxAdviceOptions.other.value
>;

/**
 * The seeded `question` is rendered as the fan's own first message and is what
 * Ramsey Connect classifies the opening turn against, so the wording is
 * load-bearing — it is not display copy. Ramsey Connect also replaces every hyphen
 * in the seeded question with a space, so these stay hyphen-free.
 *
 * Typed as a `Record` over every non-"other" `TaxAdviceOptions` value, so a
 * future option added to that enum without a matching entry here fails `tsc`
 * instead of silently rendering nothing at runtime.
 */
const adviceTopicContentByValue: Record<MappedAdviceTopic, AdviceTopicContent> =
  {
    [TaxAdviceOptions.withholding_adjustment.value]: {
      heading: 'Ask Ramsey AI your tax withholding question.',
      question: 'How much should I be withholding?',
      utmContent: 'tax_withholding'
    },
    [TaxAdviceOptions.real_estate_taxes.value]: {
      heading: 'Ask Ramsey AI your real estate tax question.',
      question: 'Do I owe taxes when I sell my house?',
      utmContent: 'real_estate'
    },
    [TaxAdviceOptions.inheritance_or_estate_taxes.value]: {
      heading: 'Ask Ramsey AI your inheritance or estate tax question.',
      question: 'Do I pay taxes on an inheritance?',
      utmContent: 'inheritance_estate'
    }
  };

/**
 * Non-"other" `TaxAdviceOptions` values in enum declaration order, so which
 * topic "wins" when several are selected tracks the enum's own order
 * (commented **Order matters!!** there) instead of a second, hand-copied one.
 */
const mappedAdviceTopicsInOrder: MappedAdviceTopic[] = Object.values(
  TaxAdviceOptions
)
  .map((option) => option.value)
  .filter(
    (value): value is MappedAdviceTopic =>
      value !== TaxAdviceOptions.other.value
  );

/**
 * Resolves the four versions of this step: one per single advice topic, plus a
 * general-heading version when several were picked. "Other" is ignored — it has
 * no question of its own, and the flow never routes here when it is the only
 * selection.
 *
 * Returns undefined only if no mapped topic was selected, which the referral
 * flow already rules out: the advice step requires at least one selection
 * (`genericCheckboxSchema`), and `generateBaselineFlow` skips this step when
 * "Other" is the only one. Callers render nothing rather than inventing a
 * question we would have no design or classification coverage for.
 */
function getAdviceTopicContent(
  taxAdvice: string[]
): AdviceTopicContent | undefined {
  const selected = mappedAdviceTopicsInOrder.filter((value) =>
    taxAdvice.includes(value)
  );
  const [firstSelected] = selected;

  if (!firstSelected) {
    return undefined;
  }

  const { heading, question, utmContent } =
    adviceTopicContentByValue[firstSelected];
  return {
    heading: selected.length > 1 ? MULTIPLE_TOPICS_HEADING : heading,
    question,
    utmContent: selected.length > 1 ? MULTIPLE_TOPICS_UTM_CONTENT : utmContent
  };
}

/**
 * Builds the cross-origin hand-off to a new Ramsey Connect chat. The referral
 * form's context is in-memory only and does not survive the origin change, so
 * everything the chat needs rides in the query string.
 */
function buildRamseyConnectChatUrl(
  question: string,
  utmContent: string,
  baseUrl: string = getRamseyConnectBaseUrl()
): string {
  const url = new URL(baseUrl);
  url.searchParams.set('q', question);
  url.searchParams.set('source', RAMSEY_CONNECT_QUESTION_SOURCE);
  url.searchParams.set('utm_source', RAMSEY_CONNECT_UTM_SOURCE);
  url.searchParams.set('utm_medium', RAMSEY_CONNECT_UTM_MEDIUM);
  url.searchParams.set('utm_campaign', RAMSEY_CONNECT_UTM_CAMPAIGN);
  url.searchParams.set('utm_content', utmContent);
  return url.toString();
}

export {
  RAMSEY_CONNECT_QUESTION_SOURCE,
  RAMSEY_CONNECT_UTM_SOURCE,
  RAMSEY_CONNECT_UTM_MEDIUM,
  RAMSEY_CONNECT_UTM_CAMPAIGN,
  MULTIPLE_TOPICS_HEADING,
  MULTIPLE_TOPICS_UTM_CONTENT,
  buildRamseyConnectChatUrl,
  getAdviceTopicContent
};
export type { AdviceTopicContent };
